import { Relationship } from '@celito.clients/types';
import { useEffect, useMemo, useState } from 'react';

import { useGetObjectData } from '../../hooks/get-object-data';
import { FieldSelectorProps } from './field-selector.model';
import { FieldSelectorView } from './field-selector.view';
import {
  ALL_OBJECTS_DEFINITION_NAME,
  copyToClipboard,
  generateMergeFieldName,
  mergeAllObjectFields,
} from './utils';

export const FieldSelectorController = (props: FieldSelectorProps) => {
  const { objectName, enableCopyClipboard = true } = props;

  const [customSearch, setCustomSearch] = useState('');

  const {
    objectData: { data: object, isLoading },
    setObjectName,
  } = useGetObjectData(objectName);

  // Fields shared by every object (e.g. task_completion_date/time) are exposed
  // through the `allObjects` metadata definition rather than the per-object one,
  // so union them into the picker list.
  const {
    objectData: { data: allObjectsDefinition },
    setObjectName: setAllObjectsName,
  } = useGetObjectData(ALL_OBJECTS_DEFINITION_NAME);

  useEffect(() => {
    if (objectName) setObjectName(objectName);
  }, [objectName, setObjectName]);

  useEffect(() => {
    setAllObjectsName(ALL_OBJECTS_DEFINITION_NAME);
  }, [setAllObjectsName]);

  const mergedObject = useMemo(
    () => mergeAllObjectFields(object, allObjectsDefinition),
    [object, allObjectsDefinition]
  );

  const onCustomSearch = (search: string) => {
    setCustomSearch(search);
  };

  const onFieldClick = (
    fieldName: string,
    relationship?: Relationship,
    appendInitalObject?: boolean,
    objectName?: string,
    firstLevel?: string
  ) => {
    setCustomSearch('');

    const toCopy = generateMergeFieldName(
      fieldName,
      relationship,
      appendInitalObject,
      objectName,
      firstLevel
    );

    enableCopyClipboard && copyToClipboard(toCopy);

    props.onCopy?.(toCopy, 1);
  };

  return (
    <FieldSelectorView
      {...{
        ...props,
        fields: mergedObject,
        isLoading,
        onFieldClick,
        customSearch,
        onCustomSearch,
      }}
    />
  );
};
